import MessageProcessingStepsLogService from "@/services/MessageProcessingStepsLog"
import ErrorHandlerService from "@/services/ErrorHandler"
import SocketService from "@/services/Socket"

import { buildMessage, generateMessageId } from "@/utils/message"

import useSocket from "@/hooks/useSocket"
import useConstantValue from "@/hooks/useConstantValue"
import useUnmount from "@/hooks/useUnmount"

import { WABAChannelMessageTemplate } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import { ISendableMessage, MessageFeature } from "@/protocols/channel"
import { ExtraSocketClientEvents, ExtraSocketClientEventPayload } from "@/@integrations/WABA/protocols/channel"
import { Chat, Message } from "@/protocols/chatGlobalStateProtocol"
import { DeepPartial } from "@/protocols/utility"

import { BuildedMessage } from "@/protocols/messages"
import WalletBalanceNotification from "@/@integrations/WABA/components/WalletBalanceNotification"

type OnWABAMessageTemplateSelectParam = {
	wabaChannelMessageTemplate: WABAChannelMessageTemplate
	onMessageSent: (sendableMessage: ISendableMessage, sentMessage: Message) => void
	updateMessageOnStore: (messageId: string, data: Partial<Message>) => Promise<void>
	addMessageOnStore: (message: Message) => Promise<void>
	replaceTemplateVariables: (wabaChannelMessageTemplateId: number, clientId: number) => Promise<BuildedMessage>
	chatOnStore: Chat | undefined
	replyMessage: Message | undefined
	messageFeature: MessageFeature
}

type SetupChatListenersParam = {
	updateChatOnStore: (chatId: number, data: DeepPartial<Chat>) => Promise<void>
	getChatOnStore: (chatId: number) => Chat | undefined
}

/**
 * This file cannot make API calls directly since it is implemented by ActiveCampaignChatQuickView
 * API calls must be made by parameter functions
 */
const useWABAChat = () => {
	const socket = useSocket()
	const customSocket = useConstantValue(new SocketService())

	const onWABAMessageTemplateSelect = async (param: OnWABAMessageTemplateSelectParam) => {
		const messageId = generateMessageId()

		try {
			const {
				chatOnStore,
				replyMessage,
				onMessageSent,
				addMessageOnStore,
				updateMessageOnStore,
				wabaChannelMessageTemplate
			} = param

			const buildedMessage = wabaChannelMessageTemplate.message

			const messageProcessingStepsLog = new MessageProcessingStepsLogService({
				inboxChannelChatMessageTempId: messageId,
				onStepChanged: (messageId, processingSteps) => updateMessageOnStore(messageId, { inboxChannelChatMessageLog: { processingSteps } })
			})

			if (!chatOnStore) {
				return
			}

			const { id, inboxChannelId, channelType } = chatOnStore

			const newMessage = await messageProcessingStepsLog.trackStep("create_temporary_message", async () => (
				buildMessage(messageId, {
					inboxChannelChatId: id,
					inboxChannelId,
					type: buildedMessage.type,
					replyMessage: replyMessage
				})
			))

			const buildedMessageWithReplacedVariables = await param.replaceTemplateVariables(wabaChannelMessageTemplate.id, chatOnStore?.client?.id)

			newMessage.content = buildedMessageWithReplacedVariables.content
			newMessage.extraData = buildedMessageWithReplacedVariables.extraData
			newMessage.wabaChannelMessageTemplate = {
				id: wabaChannelMessageTemplate.id
			}

			messageProcessingStepsLog.trackStep("show_temporary_message_on_frontend", async () => {
				await addMessageOnStore(newMessage)
			})

			await messageProcessingStepsLog.trackStep("communicate_channel_server_from_frontend", async () => {
				const message = await socket.sendMessage({
					channelType,
					inboxChannelId,
					inboxChannelChatId: id,
					content: newMessage.content,
					type: newMessage.type,
					replyMessageId: newMessage.replyMessage?.id,
					wabaChannelMessageTemplateId: newMessage.wabaChannelMessageTemplate?.id,
					extraData: newMessage.extraData,
					tempMessageId: newMessage.id,
					processingSteps: messageProcessingStepsLog.currentProcessingSteps,
					feature: param.messageFeature
				})

				onMessageSent(newMessage, {
					...newMessage,
					...message
				})
			})
		} catch (error) {
			const sendMessageError = error as Error

			ErrorHandlerService.handle(sendMessageError)
			param.updateMessageOnStore(messageId, { status: "not-sent", inboxChannelChatMessageLog: { error: sendMessageError.message } })
		}
	}

	const setupChatListeners = (param: SetupChatListenersParam) => {
		const {
			getChatOnStore,
			updateChatOnStore
		} = param

		customSocket.on<ExtraSocketClientEventPayload<"WABAChatCommunicationPermissionUpdated">, ExtraSocketClientEvents>("WABAChatCommunicationPermissionUpdated", async (data) => {
			const chat = getChatOnStore(data.inboxChannelChatId)

			if (chat) {
				await updateChatOnStore(data.inboxChannelChatId, {
					wabaChannelChatCommunicationPermission: {
						status: data.status,
						scheduledPauseAt: data.scheduledPauseAt
					}
				})
			}
		})

		customSocket.on<ExtraSocketClientEventPayload<"WABAWalletBalanceNotification">, ExtraSocketClientEvents>("WABAWalletBalanceNotification", async (balance) => {
			if (!balance.isOnSecureBalanceZone) {
				WalletBalanceNotification.open({
					instanceId: balance.instanceId
				})
			}
		})
	}

	useUnmount(() => {
		customSocket.dispose()
	})

	return {
		onWABAMessageTemplateSelect,
		setupChatListeners
	}
}

export default useWABAChat
